/**
 * NPC Configuration Data
 * Ported from OriginalGame/src/data/npcConfig.js
 * 
 * This file contains static configuration for all NPCs including:
 * - Favorite items (price multipliers at different reputation levels)
 * - Trading items (items NPCs sell)
 * - Need items (items NPCs need from player)
 * - Gift items (items NPCs give to player)
 * - Map coordinates
 */

import type { NPCGiftConfig, NPCConfigMap } from '@/common/types/npc.types'

/**
 * NPC Gift Configuration
 * Used for random gifts when NPCs visit player
 */
export const npcGiftConfig: NPCGiftConfig = {
  produceValue: 4,
  produceList: [
    { itemId: "1101**", weight: 120 },
    { itemId: "1103*1", weight: 50 },
    { itemId: "1104*1", weight: 10 },
    { itemId: "1105011", weight: 10 },
    { itemId: "1105022", weight: 10 },
    { itemId: "1302*1", weight: 10 }
  ]
}

/**
 * NPC Configuration Map
 * Contains configuration for all 7 NPCs
 * 
 * Structure:
 * - favorite: Array of 11 arrays (reputation 0-10), each containing favorite items with price multipliers
 * - trading: Array of 11 arrays (reputation 0-10), each containing items NPC sells (can be null)
 * - needItem: Array of 11 items (reputation 0-10), items NPC needs from player (can be null)
 * - gift: Array of 11 gifts (reputation 0-10), gifts NPC gives (can be null)
 * - gift_extra: Array of 11 extra gifts (reputation 0-10), for Social talent (can be null)
 * - coordinate: Map position {x, y}
 */
export const npcConfig: NPCConfigMap = {
  "1": {
    id: 1,
    favorite: [
      [{ itemId: 1105022, price: 1.2 }, { itemId: 1105033, price: 1.2 }, { itemId: 1305011, price: 1.5 }, { itemId: 1305012, price: 1.5 }],
      [{ itemId: 1105022, price: 1.2 }, { itemId: 1105033, price: 1.2 }, { itemId: 1305011, price: 1.5 }, { itemId: 1305012, price: 1.5 }],
      [{ itemId: 1105022, price: 1.2 }, { itemId: 1105033, price: 1.2 }, { itemId: 1305011, price: 1.4 }, { itemId: 1305012, price: 1.4 }],
      [{ itemId: 1105022, price: 1.2 }, { itemId: 1105033, price: 1.2 }, { itemId: 1305011, price: 1.4 }, { itemId: 1305012, price: 1.4 }],
      [{ itemId: 1105022, price: 1.2 }, { itemId: 1105033, price: 1.2 }, { itemId: 1305011, price: 1.4 }, { itemId: 1305012, price: 1.4 }],
      [{ itemId: 1105022, price: 1.2 }, { itemId: 1105033, price: 1.2 }, { itemId: 1305011, price: 1.3 }, { itemId: 1305012, price: 1.3 }],
      [{ itemId: 1105022, price: 1.2 }, { itemId: 1105033, price: 1.2 }, { itemId: 1305011, price: 1.3 }, { itemId: 1305012, price: 1.3 }],
      [{ itemId: 1105022, price: 1.2 }, { itemId: 1105033, price: 1.2 }, { itemId: 1305011, price: 1.3 }, { itemId: 1305012, price: 1.3 }],
      [{ itemId: 1105022, price: 1.2 }, { itemId: 1105033, price: 1.2 }, { itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }],
      [{ itemId: 1105022, price: 1.2 }, { itemId: 1105033, price: 1.2 }, { itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }],
      [{ itemId: 1105022, price: 1.2 }, { itemId: 1105033, price: 1.2 }, { itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }]
    ],
    trading: [
      [{ itemId: 1305011, num: 3 }],
      [{ itemId: 1305011, num: 2 }],
      [{ itemId: 1303033, num: 2 }],
      [{ itemId: 1305011, num: 2 }],
      [{ itemId: 1305011, num: 2 }],
      [{ itemId: 1305011, num: 2 }],
      [{ itemId: 1303033, num: 2 }],
      [{ itemId: 1305011, num: 2 }, { itemId: 1305011, num: 1 }],
      [{ itemId: 1305011, num: 2 }],
      [{ itemId: 1305011, num: 2 }],
      [{ itemId: 1305011, num: 2 }, { itemId: 1303033, num: 2 }]
    ],
    needItem: [
      { itemId: 1105022, num: 1 },
      { itemId: 1105022, num: 1 },
      { itemId: 1105022, num: 1 },
      { itemId: 1105022, num: 2 },
      { itemId: 1105022, num: 2 },
      { itemId: 1105022, num: 2 },
      { itemId: 1105022, num: 3 },
      { itemId: 1105022, num: 3 },
      { itemId: 1105022, num: 3 },
      { itemId: 1105022, num: 4 },
      null
    ],
    gift: [
      null,
      { itemId: "1305012", num: "30" },
      null,
      { siteId: "203" },
      null,
      { itemId: "1303022", num: "2" },
      null,
      { itemId: "1303012", num: "2" },
      null,
      { itemId: "1305012", num: "80" },
      { siteId: "20" }
    ],
    gift_extra: [
      null,
      null,
      { itemId: "1305012", num: "10" },
      null,
      null,
      null,
      { itemId: "1303022", num: "1" },
      null,
      { itemId: "1303012", num: "1" },
      null,
      { itemId: "1305012", num: "25" }
    ],
    coordinate: { x: 386, y: 211 }
  },
  "2": {
    id: 2,
    favorite: [
      [{ itemId: 1103011, price: 1.2 }, { itemId: 1103022, price: 1.2 }, { itemId: 1103033, price: 1.2 }, { itemId: 1103041, price: 1.2 }, { itemId: 1103052, price: 1.2 }, { itemId: 1103063, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1103083, price: 1.2 }, { itemId: 1104011, price: 1.5 }, { itemId: 1104021, price: 1.5 }, { itemId: 1104043, price: 1.5 }],
      [{ itemId: 1103011, price: 1.2 }, { itemId: 1103022, price: 1.2 }, { itemId: 1103033, price: 1.2 }, { itemId: 1103041, price: 1.2 }, { itemId: 1103052, price: 1.2 }, { itemId: 1103063, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1103083, price: 1.2 }, { itemId: 1104011, price: 1.5 }, { itemId: 1104021, price: 1.5 }, { itemId: 1104043, price: 1.5 }],
      [{ itemId: 1103011, price: 1.2 }, { itemId: 1103022, price: 1.2 }, { itemId: 1103033, price: 1.2 }, { itemId: 1103041, price: 1.2 }, { itemId: 1103052, price: 1.2 }, { itemId: 1103063, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1103083, price: 1.2 }, { itemId: 1104011, price: 1.4 }, { itemId: 1104021, price: 1.4 }, { itemId: 1104043, price: 1.4 }],
      [{ itemId: 1103011, price: 1.2 }, { itemId: 1103022, price: 1.2 }, { itemId: 1103033, price: 1.2 }, { itemId: 1103041, price: 1.2 }, { itemId: 1103052, price: 1.2 }, { itemId: 1103063, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1103083, price: 1.2 }, { itemId: 1104011, price: 1.4 }, { itemId: 1104021, price: 1.4 }, { itemId: 1104043, price: 1.4 }],
      [{ itemId: 1103011, price: 1.2 }, { itemId: 1103022, price: 1.2 }, { itemId: 1103033, price: 1.2 }, { itemId: 1103041, price: 1.2 }, { itemId: 1103052, price: 1.2 }, { itemId: 1103063, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1103083, price: 1.2 }, { itemId: 1104011, price: 1.4 }, { itemId: 1104021, price: 1.4 }, { itemId: 1104043, price: 1.4 }],
      [{ itemId: 1103011, price: 1.2 }, { itemId: 1103022, price: 1.2 }, { itemId: 1103033, price: 1.2 }, { itemId: 1103041, price: 1.2 }, { itemId: 1103052, price: 1.2 }, { itemId: 1103063, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1103083, price: 1.2 }, { itemId: 1104011, price: 1.3 }, { itemId: 1104021, price: 1.3 }, { itemId: 1104043, price: 1.3 }],
      [{ itemId: 1103011, price: 1.2 }, { itemId: 1103022, price: 1.2 }, { itemId: 1103033, price: 1.2 }, { itemId: 1103041, price: 1.2 }, { itemId: 1103052, price: 1.2 }, { itemId: 1103063, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1103083, price: 1.2 }, { itemId: 1104011, price: 1.3 }, { itemId: 1104021, price: 1.3 }, { itemId: 1104043, price: 1.3 }],
      [{ itemId: 1103011, price: 1.2 }, { itemId: 1103022, price: 1.2 }, { itemId: 1103033, price: 1.2 }, { itemId: 1103041, price: 1.2 }, { itemId: 1103052, price: 1.2 }, { itemId: 1103063, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1103083, price: 1.2 }, { itemId: 1104011, price: 1.3 }, { itemId: 1104021, price: 1.3 }, { itemId: 1104043, price: 1.3 }],
      [{ itemId: 1103011, price: 1.2 }, { itemId: 1103022, price: 1.2 }, { itemId: 1103033, price: 1.2 }, { itemId: 1103041, price: 1.2 }, { itemId: 1103052, price: 1.2 }, { itemId: 1103063, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1103083, price: 1.2 }, { itemId: 1104011, price: 1.2 }, { itemId: 1104021, price: 1.2 }, { itemId: 1104043, price: 1.2 }],
      [{ itemId: 1103011, price: 1.2 }, { itemId: 1103022, price: 1.2 }, { itemId: 1103033, price: 1.2 }, { itemId: 1103041, price: 1.2 }, { itemId: 1103052, price: 1.2 }, { itemId: 1103063, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1103083, price: 1.2 }, { itemId: 1104011, price: 1.2 }, { itemId: 1104021, price: 1.2 }, { itemId: 1104043, price: 1.2 }],
      [{ itemId: 1103011, price: 1.2 }, { itemId: 1103022, price: 1.2 }, { itemId: 1103033, price: 1.2 }, { itemId: 1103041, price: 1.2 }, { itemId: 1103052, price: 1.2 }, { itemId: 1103063, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1103083, price: 1.2 }, { itemId: 1104011, price: 1 }, { itemId: 1104021, price: 1 }, { itemId: 1104043, price: 1 }]
    ],
    trading: [
      [{ itemId: 1104021, num: 1 }],
      null,
      [{ itemId: 1104011, num: 1 }],
      null,
      [{ itemId: 1104021, num: 1 }],
      [{ itemId: 1104043, num: 1 }],
      [{ itemId: 1104011, num: 1 }],
      null,
      [{ itemId: 1104011, num: 1 }],
      [{ itemId: 1107052, num: 1 }],
      null
    ],
    needItem: [
      { itemId: 1103011, num: 2 },
      { itemId: 1103011, num: 2 },
      { itemId: 1103011, num: 1 },
      { itemId: 1103033, num: 1 },
      { itemId: 1103033, num: 1 },
      { itemId: 1103033, num: 1 },
      { itemId: 1103052, num: 1 },
      { itemId: 1103052, num: 1 },
      { itemId: 1103052, num: 1 },
      { itemId: 1103074, num: 1 },
      null
    ],
    gift: [
      null,
      null,
      { itemId: "1104011", num: "3" },
      null,
      { siteId: "51" },
      null,
      { itemId: "1104043", num: "2" },
      null,
      { siteId: "52" },
      null,
      { itemId: "1106054", num: "1" }
    ],
    gift_extra: [
      null,
      null,
      null,
      { itemId: "1104011", num: "1" },
      null,
      null,
      null,
      { itemId: "1104043", num: "1" },
      null,
      { itemId: "1106054", num: "1" },
      null
    ],
    coordinate: { x: 380, y: 326 }
  },
  "3": {
    id: 3,
    favorite: [
      [{ itemId: 1302011, price: 1.2 }, { itemId: 1302021, price: 1.2 }, { itemId: 1302043, price: 1.2 }, { itemId: 1101011, price: 1.5 }, { itemId: 1101031, price: 1.5 }],
      [{ itemId: 1302011, price: 1.2 }, { itemId: 1302021, price: 1.2 }, { itemId: 1302043, price: 1.2 }, { itemId: 1101011, price: 1.5 }, { itemId: 1101031, price: 1.5 }],
      [{ itemId: 1302011, price: 1.2 }, { itemId: 1302021, price: 1.2 }, { itemId: 1302043, price: 1.2 }, { itemId: 1101011, price: 1.4 }, { itemId: 1101031, price: 1.4 }],
      [{ itemId: 1302011, price: 1.2 }, { itemId: 1302021, price: 1.2 }, { itemId: 1302043, price: 1.2 }, { itemId: 1101011, price: 1.4 }, { itemId: 1101031, price: 1.4 }],
      [{ itemId: 1302011, price: 1.2 }, { itemId: 1302021, price: 1.2 }, { itemId: 1302043, price: 1.2 }, { itemId: 1101011, price: 1.4 }, { itemId: 1101031, price: 1.4 }],
      [{ itemId: 1302011, price: 1.2 }, { itemId: 1302021, price: 1.2 }, { itemId: 1302043, price: 1.2 }, { itemId: 1101011, price: 1.3 }, { itemId: 1101031, price: 1.3 }],
      [{ itemId: 1302011, price: 1.2 }, { itemId: 1302021, price: 1.2 }, { itemId: 1302043, price: 1.2 }, { itemId: 1101011, price: 1.3 }, { itemId: 1101031, price: 1.3 }],
      [{ itemId: 1302011, price: 1.2 }, { itemId: 1302021, price: 1.2 }, { itemId: 1302043, price: 1.2 }, { itemId: 1101011, price: 1.3 }, { itemId: 1101031, price: 1.3 }],
      [{ itemId: 1302011, price: 1.2 }, { itemId: 1302021, price: 1.2 }, { itemId: 1302043, price: 1.2 }, { itemId: 1101011, price: 1.2 }, { itemId: 1101031, price: 1.2 }],
      [{ itemId: 1302011, price: 1.2 }, { itemId: 1302021, price: 1.2 }, { itemId: 1302043, price: 1.2 }, { itemId: 1101011, price: 1.2 }, { itemId: 1101031, price: 1.2 }],
      [{ itemId: 1302011, price: 1.2 }, { itemId: 1302021, price: 1.2 }, { itemId: 1302043, price: 1.2 }, { itemId: 1101011, price: 1 }, { itemId: 1101031, price: 1 }]
    ],
    trading: [
      [{ itemId: 1101011, num: 10 }, { itemId: 1101031, num: 5 }],
      [{ itemId: 1101011, num: 2 }],
      [{ itemId: 1101011, num: 2 }, { itemId: 1101031, num: 1 }],
      [{ itemId: 1101011, num: 2 }],
      [{ itemId: 1101011, num: 2 }, { itemId: 1101031, num: 1 }],
      [{ itemId: 1101011, num: 2 }],
      [{ itemId: 1101011, num: 2 }, { itemId: 1101031, num: 1 }],
      [{ itemId: 1101011, num: 2 }],
      [{ itemId: 1101011, num: 2 }, { itemId: 1101031, num: 1 }],
      [{ itemId: 1101011, num: 2 }],
      [{ itemId: 1101011, num: 2 }, { itemId: 1101031, num: 1 }]
    ],
    needItem: [
      { itemId: 1302021, num: 1 },
      { itemId: 1302021, num: 1 },
      { itemId: 1302021, num: 1 },
      { itemId: 1302021, num: 1 },
      { itemId: 1302021, num: 1 },
      { itemId: 1302021, num: 1 },
      { itemId: 1302021, num: 1 },
      { itemId: 1302021, num: 1 },
      { itemId: 1302021, num: 1 },
      { itemId: 1302043, num: 1 },
      null
    ],
    gift: [
      null,
      null,
      { itemId: "1101011", num: "10" },
      null,
      { siteId: "30" },
      null,
      { itemId: "1101031", num: "5" },
      null,
      { itemId: "1101011", num: "20" },
      null,
      { siteId: "32" }
    ],
    gift_extra: [
      null,
      { itemId: "1101011", num: "3" },
      null,
      null,
      null,
      null,
      null,
      { itemId: "1101031", num: "2" },
      null,
      { itemId: "1101011", num: "6" },
      null
    ],
    coordinate: { x: 246, y: 474 }
  },
  "4": {
    id: 4,
    favorite: [
      [{ itemId: 1105011, price: 1.2 }, { itemId: 1101021, price: 1.5 }, { itemId: 1101041, price: 1.5 }, { itemId: 1101051, price: 1.5 }, { itemId: 1302043, price: 1.5 }],
      [{ itemId: 1105011, price: 1.2 }, { itemId: 1101021, price: 1.5 }, { itemId: 1101041, price: 1.5 }, { itemId: 1101051, price: 1.5 }, { itemId: 1302043, price: 1.5 }],
      [{ itemId: 1105011, price: 1.2 }, { itemId: 1101021, price: 1.4 }, { itemId: 1101041, price: 1.4 }, { itemId: 1101051, price: 1.4 }, { itemId: 1302043, price: 1.4 }],
      [{ itemId: 1105011, price: 1.2 }, { itemId: 1101021, price: 1.4 }, { itemId: 1101041, price: 1.4 }, { itemId: 1101051, price: 1.4 }, { itemId: 1302043, price: 1.4 }],
      [{ itemId: 1105011, price: 1.2 }, { itemId: 1101021, price: 1.4 }, { itemId: 1101041, price: 1.4 }, { itemId: 1101051, price: 1.4 }, { itemId: 1302043, price: 1.4 }],
      [{ itemId: 1105011, price: 1.2 }, { itemId: 1101021, price: 1.3 }, { itemId: 1101041, price: 1.3 }, { itemId: 1101051, price: 1.3 }, { itemId: 1302043, price: 1.3 }],
      [{ itemId: 1105011, price: 1.2 }, { itemId: 1101021, price: 1.3 }, { itemId: 1101041, price: 1.3 }, { itemId: 1101051, price: 1.3 }, { itemId: 1302043, price: 1.3 }],
      [{ itemId: 1105011, price: 1.2 }, { itemId: 1101021, price: 1.3 }, { itemId: 1101041, price: 1.3 }, { itemId: 1101051, price: 1.3 }, { itemId: 1302043, price: 1.3 }],
      [{ itemId: 1105011, price: 1.2 }, { itemId: 1101021, price: 1.2 }, { itemId: 1101041, price: 1.2 }, { itemId: 1101051, price: 1.2 }, { itemId: 1302043, price: 1.2 }],
      [{ itemId: 1105011, price: 1.2 }, { itemId: 1101021, price: 1.2 }, { itemId: 1101041, price: 1.2 }, { itemId: 1101051, price: 1.2 }, { itemId: 1302043, price: 1.2 }],
      [{ itemId: 1105011, price: 1.2 }, { itemId: 1101021, price: 1 }, { itemId: 1101041, price: 1 }, { itemId: 1101051, price: 1 }, { itemId: 1302043, price: 1 }]
    ],
    trading: [
      [{ itemId: 1101021, num: 6 }],
      [{ itemId: 1101021, num: 1 }],
      [{ itemId: 1101021, num: 1 }],
      [{ itemId: 1101021, num: 1 }, { itemId: 1101041, num: 3 }],
      [{ itemId: 1101021, num: 1 }],
      [{ itemId: 1101021, num: 1 }],
      [{ itemId: 1101021, num: 1 }, { itemId: 1101051, num: 3 }],
      [{ itemId: 1101021, num: 1 }],
      [{ itemId: 1101021, num: 1 }, { itemId: 1302043, num: 1 }],
      [{ itemId: 1101021, num: 1 }],
      [{ itemId: 1101021, num: 1 }]
    ],
    needItem: [
      { itemId: 1105011, num: 4 },
      { itemId: 1105011, num: 4 },
      { itemId: 1105011, num: 4 },
      { itemId: 1105011, num: 4 },
      { itemId: 1105011, num: 4 },
      { itemId: 1105011, num: 4 },
      { itemId: 1105011, num: 4 },
      { itemId: 1105011, num: 4 },
      { itemId: 1105011, num: 4 },
      { itemId: 1105011, num: 4 },
      null
    ],
    gift: [
      null,
      { itemId: "1101041", num: "3" },
      { itemId: "1102042", num: "2" },
      null,
      { siteId: "43" },
      null,
      { itemId: "1101051", num: "3" },
      { siteId: "41" },
      null,
      { itemId: "1302043", num: "1" }
    ],
    gift_extra: [
      null,
      null,
      { itemId: "1101041", num: "1" },
      { itemId: "1102042", num: "1" },
      null,
      null,
      null,
      { itemId: "1101051", num: "1" },
      null,
      { itemId: "1302043", num: "1" },
      null
    ],
    coordinate: { x: 483, y: 371 }
  },
  "5": {
    id: 5,
    favorite: [
      [{ itemId: 1101073, price: 1.2 }],
      [{ itemId: 1101073, price: 1.2 }],
      [{ itemId: 1101073, price: 1.2 }],
      [{ itemId: 1101073, price: 1.2 }],
      [{ itemId: 1101073, price: 1.2 }],
      [{ itemId: 1101073, price: 1.2 }],
      [{ itemId: 1101073, price: 1.2 }],
      [{ itemId: 1101073, price: 1.2 }],
      [{ itemId: 1101073, price: 1.2 }],
      [{ itemId: 1101073, price: 1.2 }],
      [{ itemId: 1101073, price: 1.2 }]
    ],
    trading: [
      [{ itemId: 1107012, num: 1 }],
      null,
      [{ itemId: 1107022, num: 1 }],
      null,
      [{ itemId: 1107032, num: 1 }],
      null,
      [{ itemId: 1107042, num: 1 }],
      null,
      null,
      [{ itemId: 1303044, num: 1 }],
      [{ itemId: 1303044, num: 1 }]
    ],
    needItem: [
      { itemId: 1101073, num: 4 },
      { itemId: 1101073, num: 4 },
      { itemId: 1101073, num: 6 },
      { itemId: 1101073, num: 6 },
      { itemId: 1101073, num: 8 },
      { itemId: 1101073, num: 8 },
      { itemId: 1101073, num: 8 },
      { itemId: 1101073, num: 10 },
      { itemId: 1101073, num: 10 },
      { itemId: 1101073, num: 16 },
      { itemId: 1101073, num: 22 }
    ],
    gift: [
      null,
      { itemId: "1107012", num: "1" },
      null,
      null,
      null,
      { itemId: "1107022", num: "1" },
      null,
      null,
      { itemId: "1107032", num: "1" },
      null,
      { itemId: "1102053", num: "1" }
    ],
    gift_extra: [
      null,
      null,
      null,
      { itemId: "1303044", num: "1" },
      null,
      null,
      null,
      { itemId: "1303044", num: "1" },
      null,
      null,
      null
    ],
    coordinate: { x: 246, y: 730 }
  },
  "6": {
    id: 6,
    favorite: [
      [{ itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }, { itemId: 1103074, price: 1.5 }, { itemId: 1105022, price: 1.5 }],
      [{ itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }, { itemId: 1103074, price: 1.5 }, { itemId: 1105022, price: 1.5 }],
      [{ itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }, { itemId: 1103074, price: 1.4 }, { itemId: 1105022, price: 1.4 }],
      [{ itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }, { itemId: 1103074, price: 1.4 }, { itemId: 1105022, price: 1.4 }],
      [{ itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }, { itemId: 1103074, price: 1.3 }, { itemId: 1105022, price: 1.3 }],
      [{ itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }, { itemId: 1103074, price: 1.3 }, { itemId: 1105022, price: 1.3 }],
      [{ itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1105022, price: 1.2 }],
      [{ itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }, { itemId: 1103074, price: 1.2 }, { itemId: 1105022, price: 1.2 }],
      [{ itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }, { itemId: 1103074, price: 1.1 }, { itemId: 1105022, price: 1.1 }],
      [{ itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }, { itemId: 1103074, price: 1.1 }, { itemId: 1105022, price: 1.1 }],
      [{ itemId: 1305011, price: 1.2 }, { itemId: 1305012, price: 1.2 }, { itemId: 1103074, price: 1.1 }, { itemId: 1105022, price: 1.1 }]
    ],
    trading: [
      [{ itemId: 1105022, num: 1 }],
      [{ itemId: 1105022, num: 1 }, { itemId: 1103074, num: 1 }],
      [{ itemId: 1105022, num: 1 }],
      [{ itemId: 1105022, num: 1 }, { itemId: 1103074, num: 1 }],
      [{ itemId: 1105022, num: 1 }],
      [{ itemId: 1105022, num: 1 }, { itemId: 1103074, num: 1 }],
      [{ itemId: 1105022, num: 1 }],
      [{ itemId: 1105022, num: 1 }, { itemId: 1103074, num: 1 }],
      [{ itemId: 1105022, num: 1 }],
      [{ itemId: 1105022, num: 1 }, { itemId: 1103074, num: 1 }],
      [{ itemId: 1105022, num: 1 }]
    ],
    needItem: [
      { itemId: 1305012, num: 10 },
      { itemId: 1305012, num: 10 },
      { itemId: 1305012, num: 10 },
      { itemId: 1305012, num: 20 },
      { itemId: 1305012, num: 40 },
      { itemId: 1303022, num: 1 },
      { itemId: 1303022, num: 2 },
      { itemId: 1303012, num: 2 },
      { itemId: 1303012, num: 3 },
      { itemId: 1104032, num: 1 },
      null
    ],
    gift: [
      null,
      { itemId: "1105022", num: "5" },
      { siteId: "21" },
      null,
      { itemId: "1105022", num: "5" },
      null,
      { itemId: "1105022", num: "5" },
      null,
      { itemId: "1105022", num: "5" },
      { siteId: "502" },
      { itemId: "1105022", num: "30" }
    ],
    gift_extra: [
      null,
      null,
      null,
      null,
      { itemId: "1103074", num: "5" },
      null,
      null,
      { itemId: "1105022", num: "15" },
      null,
      null,
      { itemId: "1103074", num: "10" }
    ],
    coordinate: { x: 300, y: 50 }
  },
  "7": {
    id: 7,
    favorite: [
      [{ itemId: 1301041, price: 1.2 }, { itemId: 1301011, price: 1.5 }, { itemId: 1301033, price: 1.5 }, { itemId: 1301022, price: 1.2 }, { itemId: 1301052, price: 1.5 }, { itemId: 1301063, price: 1.5 }],
      [{ itemId: 1301041, price: 1.2 }, { itemId: 1301011, price: 1.5 }, { itemId: 1301033, price: 1.5 }, { itemId: 1301022, price: 1.2 }, { itemId: 1301052, price: 1.5 }, { itemId: 1301063, price: 1.5 }],
      [{ itemId: 1301041, price: 1.2 }, { itemId: 1301011, price: 1.5 }, { itemId: 1301033, price: 1.4 }, { itemId: 1301022, price: 1.2 }, { itemId: 1301052, price: 1.5 }, { itemId: 1301063, price: 1.4 }],
      [{ itemId: 1301041, price: 1.2 }, { itemId: 1301011, price: 1.4 }, { itemId: 1301033, price: 1.4 }, { itemId: 1301022, price: 1.2 }, { itemId: 1301052, price: 1.4 }, { itemId: 1301063, price: 1.4 }],
      [{ itemId: 1301041, price: 1.2 }, { itemId: 1301011, price: 1.4 }, { itemId: 1301033, price: 1.4 }, { itemId: 1301022, price: 1.2 }, { itemId: 1301052, price: 1.4 }, { itemId: 1301063, price: 1.3 }],
      [{ itemId: 1301041, price: 1.2 }, { itemId: 1301011, price: 1.4 }, { itemId: 1301033, price: 1.3 }, { itemId: 1301022, price: 1.2 }, { itemId: 1301052, price: 1.4 }, { itemId: 1301063, price: 1.3 }],
      [{ itemId: 1301041, price: 1.2 }, { itemId: 1301011, price: 1.3 }, { itemId: 1301033, price: 1.3 }, { itemId: 1301022, price: 1.2 }, { itemId: 1301052, price: 1.3 }, { itemId: 1301063, price: 1.3 }],
      [{ itemId: 1301041, price: 1.2 }, { itemId: 1301011, price: 1.3 }, { itemId: 1301033, price: 1.3 }, { itemId: 1301022, price: 1.2 }, { itemId: 1301052, price: 1.3 }, { itemId: 1301063, price: 1.2 }],
      [{ itemId: 1301041, price: 1.2 }, { itemId: 1301011, price: 1.3 }, { itemId: 1301033, price: 1.2 }, { itemId: 1301022, price: 1.2 }, { itemId: 1301052, price: 1.3 }, { itemId: 1301063, price: 1.2 }],
      [{ itemId: 1301041, price: 1.2 }, { itemId: 1301011, price: 1.2 }, { itemId: 1301033, price: 1.2 }, { itemId: 1301022, price: 1.2 }, { itemId: 1301052, price: 1.3 }, { itemId: 1301063, price: 1.2 }],
      [{ itemId: 1301041, price: 1.2 }, { itemId: 1301011, price: 1.2 }, { itemId: 1301033, price: 1.2 }, { itemId: 1301022, price: 1.2 }, { itemId: 1301052, price: 1.2 }, { itemId: 1301063, price: 1.2 }]
    ],
    trading: [
      [{ itemId: 1101071, num: 1 }],
      [{ itemId: 1101071, num: 1 }, { itemId: 1105033, num: 1 }],
      [{ itemId: 1101071, num: 1 }],
      [{ itemId: 1101071, num: 1 }, { itemId: 1105033, num: 1 }],
      [{ itemId: 1101071, num: 1 }],
      [{ itemId: 1101071, num: 1 }, { itemId: 1105033, num: 1 }],
      [{ itemId: 1101071, num: 1 }],
      [{ itemId: 1101071, num: 1 }, { itemId: 1105033, num: 1 }],
      [{ itemId: 1101071, num: 2 }],
      [{ itemId: 1101071, num: 2 }, { itemId: 1105033, num: 1 }],
      [{ itemId: 1101071, num: 2 }]
    ],
    needItem: [
      { itemId: 1301011, num: 1 },
      { itemId: 1301022, num: 1 },
      { itemId: 1301033, num: 1 },
      { itemId: 1301041, num: 1 },
      { itemId: 1301052, num: 1 },
      { itemId: 1302043, num: 1 },
      { itemId: 1303012, num: 2 },
      { itemId: 1303044, num: 1 },
      { itemId: 1305012, num: 10 },
      { itemId: 1305012, num: 30 },
      null
    ],
    gift: [
      null,
      { itemId: "1101071", num: "5" },
      null,
      { siteId: "500" },
      { itemId: "1101071", num: "5" },
      null,
      { itemId: "1101071", num: "10" },
      { siteId: "501" },
      { itemId: "1101071", num: "10" },
      null,
      { itemId: "1105033", num: "10" }
    ],
    gift_extra: [
      null,
      null,
      null,
      null,
      { itemId: "1101071", num: "10" },
      null,
      null,
      { itemId: "1101071", num: "10" },
      null,
      null,
      { itemId: "1105033", num: "10" }
    ],
    coordinate: { x: 310, y: 250 }
  }
}

